import React, { useCallback, useEffect, useRef, useState } from "react";

interface ResizableDividerProps {
  onResize: (newHeight: number) => void;
  containerHeight: number;
  currentTopHeight: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
  minTopHeight?: number;
  minBottomHeight?: number;
}

export const ResizableDivider: React.FC<ResizableDividerProps> = ({
  onResize,
  containerHeight,
  currentTopHeight,
  containerRef,
  minTopHeight = 200,
  minBottomHeight = 200,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startYRef.current = e.clientY;
      startHeightRef.current = currentTopHeight;
    },
    [currentTopHeight],
  );

  useEffect(() => {
    if (!isDragging) return;

    let rafId: number | null = null;
    let lastUpdateTime = 0;
    const throttleMs = 16; // ~60fps

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || !dividerRef.current) return;

      const now = performance.now();
      if (now - lastUpdateTime < throttleMs) {
        // Throttle updates to prevent excessive re-renders
        if (rafId === null) {
          rafId = requestAnimationFrame(() => {
            rafId = null;
            lastUpdateTime = performance.now();
            updateHeight(e);
          });
        }
        return;
      }

      lastUpdateTime = now;
      updateHeight(e);
    };

    const updateHeight = (e: MouseEvent) => {
      if (!containerRef.current) return;

      // Get current container height dynamically
      const currentContainerHeight = containerRef.current.clientHeight;

      // Calculate mouse position relative to container
      const containerRect = containerRef.current.getBoundingClientRect();
      const mouseYRelativeToContainer = e.clientY - containerRect.top;

      // Constrain the height within min/max bounds
      // Account for divider height (4px) and minimum bottom height
      const dividerHeight = 4;
      const maxHeight =
        currentContainerHeight - minBottomHeight - dividerHeight;
      const constrainedHeight = Math.max(
        minTopHeight,
        Math.min(maxHeight, mouseYRelativeToContainer),
      );

      onResize(constrainedHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: true });
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    isDragging,
    onResize,
    containerHeight,
    minTopHeight,
    minBottomHeight,
    containerRef,
  ]);

  return (
    <div
      ref={dividerRef}
      className={`relative flex items-center justify-center bg-gray-300 hover:bg-gray-400 transition-colors ${
        isDragging ? "bg-gray-500 cursor-grabbing" : "cursor-row-resize"
      }`}
      style={{ height: "4px", userSelect: "none" }}
      onMouseDown={handleMouseDown}
      data-testid="resizable-divider"
    >
      <div className="w-12 h-1 bg-gray-500 rounded-full" />
    </div>
  );
};
